import { ApiError, type ConnectionBanner, connectionBannerFromError } from "../../api";

/** Hide longer than this is treated as sleep, not a quick tab switch. */
export const WAKE_HIDDEN_MS = 30_000;
/** HTML arrived, but the app still has not painted. */
export const BOOT_RELOAD_MS = 15_000;
export const PAGE_REFRESH_EVENT = "klt-rms-refresh-page";

export type WakeClock = {
  hiddenAt: number | null;
  pendingOnline: boolean;
  refreshInFlight: boolean;
};

export type WakeEvent =
  | { kind: "visibility"; state: "hidden" | "visible"; now: number }
  | { kind: "online"; visible: boolean; now: number };

export function createWakeClock(): WakeClock {
  return { hiddenAt: null, pendingOnline: false, refreshInFlight: false };
}

export function noteWakeRefreshSettled(clock: WakeClock): WakeClock {
  return { ...clock, refreshInFlight: false };
}

export function reduceWake(clock: WakeClock, event: WakeEvent): { clock: WakeClock; recover: boolean } {
  if (event.kind === "visibility" && event.state === "hidden") {
    return { clock: { ...clock, hiddenAt: clock.hiddenAt ?? event.now }, recover: false };
  }
  if (clock.refreshInFlight) {
    if (event.kind === "online" && !event.visible) {
      return { clock: { ...clock, pendingOnline: true }, recover: false };
    }
    return { clock, recover: false };
  }
  if (event.kind === "online" && !event.visible) {
    return { clock: { ...clock, pendingOnline: true }, recover: false };
  }
  const hiddenMs = clock.hiddenAt == null ? 0 : event.now - clock.hiddenAt;
  const recover = (event.kind === "online" && event.visible) || clock.pendingOnline || hiddenMs > WAKE_HIDDEN_MS;
  if (!recover) {
    return { clock: { ...clock, hiddenAt: null }, recover: false };
  }
  return {
    clock: { ...clock, hiddenAt: null, pendingOnline: false, refreshInFlight: true },
    recover: true,
  };
}

export function bootWatchdogAction(rootEmpty: boolean, elapsedMs: number): "reload" | "wait" {
  if (!rootEmpty || elapsedMs < BOOT_RELOAD_MS) return "wait";
  return "reload";
}

export function shouldRefreshOpenPage(ok: boolean): boolean {
  return ok;
}

export function notifyOpenPage(ok: boolean, dispatch: (name: string) => void): void {
  if (shouldRefreshOpenPage(ok)) dispatch(PAGE_REFRESH_EVENT);
}

export type SessionFailure =
  | { kind: "signed-out" }
  | { kind: "offline"; connectionIssue: ConnectionBanner }
  | { kind: "cleared" };

function isTransientSessionError(err: unknown): boolean {
  if (err instanceof ApiError) {
    return err.status === 502 || err.status === 503 || err.status === 504;
  }
  if (err instanceof Error) {
    const raw = `${err.name} ${err.message}`;
    return (
      err.name === "AbortError" ||
      /timed out|timeout|failed to fetch|networkerror|load failed|network request failed/i.test(raw)
    );
  }
  return false;
}

export function classifySessionError(err: unknown): SessionFailure {
  if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
    return { kind: "signed-out" };
  }
  if (isTransientSessionError(err)) {
    return { kind: "offline", connectionIssue: connectionBannerFromError(err) };
  }
  return { kind: "cleared" };
}
